import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import "../../styles/DashboardHome.css";
import "../../styles/Tables.css";
import "../../styles/CarbonMethodology.css";
import EvidenceReferenceField from "../../components/EvidenceReferenceField";
import {
  approveCarbonMethodologyApi,
  canApproveEmsModule,
  fetchCarbonMethodologyApi,
  updateCarbonMethodologyApi,
} from "../../lib/api";

const EMPTY_FORM = {
  boundary_description: "",
  assumptions: "",
  exclusions: "",
  data_quality_notes: "",
  base_year: "",
  last_review_date: "",
  evidence: "",
  notes: "",
};

export default function CarbonMethodology() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [record, setRecord] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const canApprove = canApproveEmsModule("carbon_methodology");

  const loadRecord = async () => {
    try {
      setLoading(true);
      const data = await fetchCarbonMethodologyApi();
      setRecord(data || null);
      setForm({
        boundary_description: data?.boundary_description || "",
        assumptions: data?.assumptions || "",
        exclusions: data?.exclusions || "",
        data_quality_notes: data?.data_quality_notes || "",
        base_year: data?.base_year || "",
        last_review_date: data?.last_review_date || "",
        evidence: data?.evidence || "",
        notes: data?.notes || "",
      });
    } catch (err) {
      toast.error(err?.message || "Failed to load carbon methodology review");
      setRecord(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecord();
  }, []);

  const summary = useMemo(() => ([
    { label: "Status", value: String(record?.status || "draft") },
    { label: "Standard", value: "GHG Protocol" },
    { label: "Factor source", value: "DEFRA managed" },
    { label: "Last review", value: String(record?.last_review_date || "-") || "-" },
  ]), [record]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const updated = await updateCarbonMethodologyApi(form);
      setRecord(updated || null);
      toast.success("Carbon methodology review saved");
    } catch (err) {
      toast.error(err?.message || "Failed to save carbon methodology review");
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    try {
      setApproving(true);
      const notes = window.prompt("Optional approval note:", "") || "";
      const resp = await approveCarbonMethodologyApi({ notes });
      setRecord(resp?.methodology || null);
      toast.success("Carbon methodology review approved");
    } catch (err) {
      toast.error(err?.message || "Failed to approve carbon methodology review");
    } finally {
      setApproving(false);
    }
  };

  return (
    <div className="dashboard-page methodology-review-page">
      <div className="dashboard-header">
        <div>
          <p className="methodology-eyebrow">Carbon reporting setup</p>
          <h1>Carbon Methodology Review</h1>
          <p className="subtitle">
            Hindsight One applies the calculation method automatically. Confirm the organisation-specific reporting choices below.
          </p>
        </div>
      </div>

      <div className="summary-row">
        {summary.map((item) => (
          <div className="kpi-card" key={item.label}>
            <div className="kpi-label">{item.label}</div>
            <h2>{loading ? "-" : item.value}</h2>
          </div>
        ))}
      </div>

      <section className="card methodology-platform-card">
        <div>
          <p className="methodology-eyebrow">Built into Hindsight One</p>
          <h3>Platform calculation approach</h3>
          <p>
            Emissions are calculated from uploaded evidence and manual entries. The platform classifies activity data by
            scope and applies the current DEFRA factor set.
          </p>
        </div>
        <div className="methodology-platform-grid">
          <div>
            <strong>Standard</strong>
            <span>GHG Protocol Scope 1, 2 and 3</span>
          </div>
          <div>
            <strong>Conversion factors</strong>
            <span>UK Government DEFRA factors, managed by the platform administrator</span>
          </div>
          <div>
            <strong>Audit trail</strong>
            <span>Download the calculation breakdown to view quantities, factors and assumptions used</span>
          </div>
        </div>
      </section>

      <div className="card settings-card methodology-review-card">
        <div className="settings-header">
          <div>
            <p className="methodology-eyebrow">Your review</p>
            <h3>Confirm your reporting choices</h3>
          </div>
          <span className="kpi-sub">Complete the fields that describe your organisation, not the calculation engine.</span>
        </div>

        <form onSubmit={handleSave}>
          <div className="settings-grid">
            <label className="settings-field">
              <span>Base year</span>
              <input
                inputMode="numeric"
                placeholder="e.g. 2025"
                value={form.base_year}
                onChange={(e) => setField("base_year", e.target.value)}
                disabled={saving || loading}
              />
              <small>Choose the first reliable reporting year you will use for future comparisons and targets.</small>
            </label>

            <label className="settings-field">
              <span>Last review date</span>
              <input
                type="date"
                value={form.last_review_date}
                onChange={(e) => setField("last_review_date", e.target.value)}
                disabled={saving || loading}
              />
              <small>Use the date an authorised person checked this review. Revisit it at least annually.</small>
            </label>

            <EvidenceReferenceField
              label="Supporting evidence"
              value={form.evidence}
              onChange={(value) => setField("evidence", value)}
              disabled={saving || loading}
              helpText="Optional: link a reporting policy, boundary map, ownership structure or other supporting document."
            />

            <label className="settings-field methodology-wide-field">
              <span>Reporting boundary</span>
              <small>
                State which legal entities, sites and operational activities are included. For example:
                "UK operations, head office and warehouse under operational control."
              </small>
              <textarea
                rows={4}
                value={form.boundary_description}
                onChange={(e) => setField("boundary_description", e.target.value)}
                disabled={saving || loading}
                placeholder="Describe the entities, locations and activities included in this carbon report."
              />
            </label>

            <label className="settings-field methodology-wide-field">
              <span>Assumptions</span>
              <small>
                Record any estimate or default used because evidence was incomplete, such as a fuel type, passenger class
                or shared-building allocation.
              </small>
              <textarea
                rows={4}
                value={form.assumptions}
                onChange={(e) => setField("assumptions", e.target.value)}
                disabled={saving || loading}
                placeholder="List assumptions used for this organisation's data, or write 'None'."
              />
            </label>

            <label className="settings-field methodology-wide-field">
              <span>Exclusions</span>
              <small>
                List material activities or sites not yet included and why. Write "None known" if the reporting boundary is complete.
              </small>
              <textarea
                rows={3}
                value={form.exclusions}
                onChange={(e) => setField("exclusions", e.target.value)}
                disabled={saving || loading}
                placeholder="Describe exclusions and the reason for each one."
              />
            </label>

            <label className="settings-field methodology-wide-field">
              <span>Data quality notes</span>
              <small>
                Note missing months, estimated readings, incomplete supplier information or improvements planned for the next reporting cycle.
              </small>
              <textarea
                rows={3}
                value={form.data_quality_notes}
                onChange={(e) => setField("data_quality_notes", e.target.value)}
                disabled={saving || loading}
                placeholder="Summarise the quality and completeness of the available data."
              />
            </label>

            <label className="settings-field methodology-wide-field">
              <span>Additional review notes</span>
              <small>Optional: record decisions, internal reviewers or agreed follow-up actions.</small>
              <textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setField("notes", e.target.value)}
                disabled={saving || loading}
                placeholder="Optional review notes."
              />
            </label>
          </div>

          <div className="settings-actions">
            <button className="btn-primary" type="submit" disabled={saving || loading}>
              {saving ? "Saving..." : "Save review"}
            </button>
            {canApprove ? (
              <button
                className="btn-secondary"
                type="button"
                disabled={approving || loading}
                onClick={handleApprove}
              >
                {approving ? "Approving..." : "Approve review"}
              </button>
            ) : null}
          </div>
        </form>
      </div>
    </div>
  );
}
